import { useTranslation } from "react-i18next";
import type { HauptaussagenContent } from "../content-types";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

/** Read a text and multi-select its genuine main ideas (mirrors iOS
 *  HauptaussagenView: a candidate is correct when picked == is_main). Answer is
 *  the array of picked candidate ids. */
export function HauptaussagenRenderer({ content, answer, setAnswer, revealed }: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as HauptaussagenContent;
  const picked = new Set((answer as string[]) ?? []);

  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setAnswer([...next]);
  };

  return (
    <div>
      <Prompt>{c.prompt}</Prompt>
      {c.passage?.title && <div className="mt-3 text-sm font-semibold text-ink">{c.passage.title}</div>}
      {c.passage?.text && (
        <div className="mt-1 whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-sm text-ink">{c.passage.text}</div>
      )}
      <div className="mt-4 space-y-2">
        {(c.candidates ?? []).map((cand) => {
          const isPicked = picked.has(cand.id);
          let tone = isPicked ? "border-brand-500 bg-brand-50" : "border-black/10 bg-white hover:border-brand-300";
          if (revealed) {
            const ok = isPicked === cand.is_main;
            tone = ok ? "border-emerald-400 bg-emerald-50" : "border-red-400 bg-red-50";
          }
          return (
            <button
              key={cand.id}
              type="button"
              disabled={revealed}
              onClick={() => toggle(cand.id)}
              className={`flex w-full items-start gap-2 rounded-xl border px-4 py-3 text-left text-sm transition disabled:cursor-default ${tone}`}
            >
              <span className="mt-0.5">{isPicked ? "☑" : "☐"}</span>
              <span>
                {cand.text}
                {revealed && cand.is_main && (
                  <span className="mt-0.5 block text-xs font-semibold text-emerald-700">{t("Main idea")}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
